import { useRef, useState } from 'react';
import { Plus, Trash, UploadSimple } from '@phosphor-icons/react';
import type { MappedApiError } from '@mybusinessmate/api-client';
import type { DeliverableKind, StaffDeliverable } from '@mybusinessmate/schema';
import {
  DELIVERABLE_UPLOAD_MAX_BYTES,
  DELIVERABLE_UPLOAD_TYPES,
  checkDeliverableFile,
  uploadVaultFile,
} from '../../lib/asset-upload';
import { cx } from '../../lib/cx';
import { useSaveDeliverable } from '../../lib/deliverables-api';
import { toasts } from '../../lib/toast-presets';
import {
  emptyForm,
  formFromItem,
  newField,
  toInput,
  validateForm,
  type FormField,
  type VaultForm,
  type VaultFormErrors,
} from '../../lib/vault-form';
import { formatBytes } from '../../lib/vault-display';
import { Icon } from '../Icon';
import { Button } from '../ui/button';
import { Drawer } from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { SegmentedControl } from '../ui/segmented-control';
import { Select } from '../ui/select';
import { textLinkClass } from '../ui/text-link';
import { Toggle } from '../ui/toggle';
import { useToast } from '../ui/toast';
import { UploadRow } from '../ui/upload-row';

/** A name or value box inside a field block (Figma A05.11b): 40 tall, grey, a 10 radius. */
const BOX =
  'h-(--mbm-size-vault-input) min-w-0 rounded-(--mbm-component-radius-vault-field) bg-surface-field px-(--mbm-space-3) text-body-compact text-text-primary outline-none placeholder:text-text-muted focus:outline-2 focus:-outline-offset-2 focus:outline-focus-ring';

const KIND_OPTIONS: { value: DeliverableKind; label: string }[] = [
  { value: 'login', label: 'Login' },
  { value: 'file', label: 'File' },
  { value: 'link', label: 'Link' },
];

function FieldBlock({
  field,
  index,
  error,
  removable,
  onChange,
  onRemove,
  visibilityOnly = false,
}: {
  field: FormField;
  index: number;
  error: { label?: string; value?: string };
  removable: boolean;
  onChange: (patch: Partial<FormField>) => void;
  onRemove: () => void;
  /** A file has no name or value to type and can't be secret: only who may see it. */
  visibilityOnly?: boolean;
}) {
  const password = field.type === 'password';
  return (
    <div className="flex flex-col gap-(--mbm-space-2-5) rounded-(--mbm-component-radius-option-tile) border border-border-line bg-surface-card p-(--mbm-space-3-5)">
      <div className={cx('items-center gap-(--mbm-space-2-5)', visibilityOnly ? 'hidden' : 'flex')}>
        <input
          aria-label={`Field ${index + 1} name`}
          value={field.label}
          placeholder="Name"
          onChange={(e) => onChange({ label: e.target.value })}
          className={cx(BOX, 'w-(--mbm-layout-vault-field-label-width) shrink-0 text-label-medium')}
        />
        <input
          aria-label={`${field.label || `Field ${index + 1}`} value`}
          type={field.secret || password ? 'password' : 'text'}
          autoComplete="off"
          value={field.value}
          placeholder={field.stored ? '••••••••••••' : field.type === 'link' ? 'https://' : 'Value'}
          onChange={(e) => onChange({ value: e.target.value })}
          className={cx(BOX, 'flex-1')}
        />
        {removable ? (
          <button
            type="button"
            aria-label={`Remove ${field.label || 'field'}`}
            onClick={onRemove}
            className="flex size-(--mbm-space-7) shrink-0 items-center justify-center rounded-full text-text-tertiary hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
          >
            <Icon icon={Trash} size="sm" />
          </button>
        ) : null}
      </div>
      {error.label || error.value ? (
        <p role="alert" className="text-body-xsmall text-feedback-error">
          {error.label ?? error.value}
        </p>
      ) : null}
      <div className="flex items-center justify-between gap-(--mbm-space-3)">
        {visibilityOnly ? (
          <span className="text-body-small text-text-secondary">Who can download it</span>
        ) : (
          <Checkbox
            label="Secret · encrypt and hide"
            checked={field.secret || password}
            // A password is always encrypted; the box stays ticked and can't be undone.
            disabled={password}
            onChange={(e) => onChange({ secret: e.target.checked })}
          />
        )}
        <span className="flex items-center gap-(--mbm-space-2) text-body-small text-text-secondary">
          {field.visibleToClient ? 'Client can see' : 'Team only'}
          <Toggle
            checked={field.visibleToClient}
            onCheckedChange={(visibleToClient) => onChange({ visibleToClient })}
            aria-label={`${field.label || `Field ${index + 1}`} visible to the client`}
          />
        </span>
      </div>
    </div>
  );
}

/** The one file of a "File" item: pick it, watch it upload, replace or remove it. */
function FilePicker({
  field,
  businessId,
  error,
  onChange,
}: {
  field: FormField;
  businessId: string;
  error?: string;
  onChange: (patch: Partial<FormField>) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [upload, setUpload] = useState<{
    name: string;
    size: number;
    status: 'uploading' | 'failed' | 'rejected';
    progress: number;
    message?: string;
    file?: File;
  } | null>(null);

  const start = (file: File) => {
    const problem = checkDeliverableFile(file);
    if (problem)
      return setUpload({ name: file.name, size: file.size, status: 'rejected', progress: 0, message: problem });
    setUpload({ name: file.name, size: file.size, status: 'uploading', progress: 0, file });
    const handle = uploadVaultFile(file, businessId, (progress) => setUpload((u) => (u ? { ...u, progress } : u)));
    handle.promise.then(
      (key) => {
        setUpload(null);
        onChange({ file: { key, name: file.name, contentType: file.type, size: file.size } });
      },
      (err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setUpload((u) => (u ? { ...u, status: 'failed', message: (err as { message?: string })?.message } : u));
      },
    );
  };

  const stored = field.file;
  return (
    <div className="flex flex-col gap-(--mbm-space-2-5)">
      <ul className="flex flex-col gap-(--mbm-space-2)">
        {upload ? (
          <UploadRow
            name={upload.name}
            sizeLabel={formatBytes(upload.size)}
            status={upload.status}
            progress={upload.progress}
            message={upload.message}
            onCancel={() => setUpload(null)}
            onRemove={() => setUpload(null)}
            onRetry={upload.file ? () => start(upload.file!) : undefined}
          />
        ) : stored ? (
          <UploadRow
            name={stored.name}
            sizeLabel={formatBytes(stored.size)}
            status="done"
            onRemove={() => onChange({ file: undefined })}
          />
        ) : null}
      </ul>
      {!upload && !stored ? (
        <button
          type="button"
          onClick={() => input.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const file = e.dataTransfer.files[0];
            if (file) start(file);
          }}
          className="flex items-center justify-center gap-(--mbm-space-2) rounded-(--mbm-radius-sm) border-[1.5px] border-dashed border-border-default px-(--mbm-space-3-5) py-(--mbm-space-4) text-label-medium text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
        >
          <Icon icon={UploadSimple} size="sm" />
          Add a file
          <span className="text-body-small text-text-tertiary">
            · up to {formatBytes(DELIVERABLE_UPLOAD_MAX_BYTES)}
          </span>
        </button>
      ) : null}
      <input
        ref={input}
        type="file"
        hidden
        accept={DELIVERABLE_UPLOAD_TYPES.join(',')}
        aria-label="Choose a file for the vault"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) start(file);
          e.target.value = '';
        }}
      />
      {error ? (
        <p role="alert" className="text-body-xsmall text-feedback-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * "Add to vault" (Figma A05.11b): what kind of thing, what it is called, which project it is for, and its fields, each
 * with the two choices that matter: encrypt and hide it, and whether the client may see it. Editing an item opens the
 * same sheet; a secret is never sent back, so a secret field stays as dots until a new value is typed.
 */
export function VaultDrawer({
  open,
  onClose,
  businessId,
  businessName,
  projects,
  item,
}: {
  open: boolean;
  onClose: () => void;
  businessId: string;
  businessName: string;
  projects: { id: string; name: string }[];
  /** The item being edited; null to add a new one. */
  item: StaffDeliverable | null;
}) {
  const toast = useToast();
  const save = useSaveDeliverable(businessId);
  const [form, setForm] = useState<VaultForm>(() => (item ? formFromItem(item) : emptyForm('login')));
  const [errors, setErrors] = useState<VaultFormErrors>({});
  const [asked, setAsked] = useState(false);
  const editing = item !== null;

  const patch = (change: Partial<VaultForm>) => {
    setForm((f) => ({ ...f, ...change }));
  };
  const patchField = (key: string, change: Partial<FormField>) =>
    setForm((f) => ({ ...f, fields: f.fields.map((x) => (x.key === key ? { ...x, ...change } : x)) }));

  const errorsNow = asked ? validateForm(form) : errors;
  const fieldError = (index: number) => ({
    label: errorsNow[`fields.${index}.label`],
    value: errorsNow[`fields.${index}.value`],
  });

  const submit = () => {
    setAsked(true);
    if (Object.keys(validateForm(form)).length > 0) return;
    save.mutate(
      { id: item?.id, input: toInput(form) },
      {
        onSuccess: () => {
          toast(toasts.saved(editing ? 'Your changes are in the vault.' : 'Added to the vault.'));
          onClose();
        },
        onError: (err) => {
          const e = err as Partial<MappedApiError>;
          // The server found something the form could not: show it where it belongs.
          if (e.fieldErrors?.length) {
            setAsked(false);
            setErrors(Object.fromEntries(e.fieldErrors.map((x) => [x.field, x.message])));
            return;
          }
          toast(toasts.failed('Couldn’t save', e.message ?? 'Something went wrong on our side. Try again.', submit));
        },
      },
    );
  };

  const changeKind = (kind: DeliverableKind) => {
    setAsked(false);
    setErrors({});
    // Keep what was typed in the title; the fields are the kind's own.
    setForm((f) => ({ ...emptyForm(kind), title: f.title, projectId: f.projectId, description: f.description }));
  };

  const login = form.kind === 'login';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="wide"
      title={editing ? 'Edit item' : 'Add to vault'}
      description={businessName}
      footer={
        <>
          <Button variant="secondary" size="dialog" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button size="dialog" onClick={submit} loading={save.isPending}>
            {editing ? 'Save changes' : 'Save to vault'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-(--mbm-space-4-5)">
        {editing ? null : (
          <SegmentedControl
            shape="pill"
            size="lg"
            options={KIND_OPTIONS}
            value={form.kind}
            onChange={changeKind}
            className="self-start"
          />
        )}

        <div className="grid grid-cols-2 gap-(--mbm-space-3)">
          <Input
            variant="filled"
            label="Title"
            value={form.title}
            error={errorsNow.title}
            onChange={(e) => patch({ title: e.target.value })}
            autoComplete="off"
          />
          <Select
            variant="filled"
            label="Link to"
            value={form.projectId}
            error={errorsNow.projectId}
            onChange={(e) => patch({ projectId: e.target.value })}
          >
            <option value="">The whole business</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex flex-col gap-(--mbm-space-3)">
          <div className="flex items-center justify-between">
            <h3 className="text-heading-h4 text-text-primary">
              {login ? 'Fields' : form.kind === 'file' ? 'File' : 'Link'}
            </h3>
            {login ? (
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, fields: [...f.fields, newField()] }))}
                className={cx(textLinkClass, 'inline-flex items-center gap-(--mbm-space-1)')}
              >
                <Icon icon={Plus} size="xs" />
                Add field
              </button>
            ) : null}
          </div>

          {errorsNow.fields ? (
            <p role="alert" className="text-body-small text-feedback-error">
              {errorsNow.fields}
            </p>
          ) : null}

          {form.kind === 'file' ? (
            <>
              <FilePicker
                field={form.fields[0]!}
                businessId={businessId}
                error={errorsNow['fields.0.file']}
                onChange={(change) => patchField(form.fields[0]!.key, change)}
              />
              <FieldBlock
                field={form.fields[0]!}
                index={0}
                error={fieldError(0)}
                removable={false}
                onChange={(change) => patchField(form.fields[0]!.key, change)}
                onRemove={() => undefined}
                visibilityOnly
              />
            </>
          ) : (
            form.fields.map((field, index) => (
              <FieldBlock
                key={field.key}
                field={field}
                index={index}
                error={fieldError(index)}
                removable={login && form.fields.length > 1}
                onChange={(change) => patchField(field.key, change)}
                onRemove={() => setForm((f) => ({ ...f, fields: f.fields.filter((x) => x.key !== field.key) }))}
              />
            ))
          )}
        </div>

        <p className="text-body-xsmall text-text-tertiary">
          Secret values are encrypted and only shown after someone confirms it’s them. Every reveal is logged. Team-only
          fields never reach the client.
        </p>
      </div>
    </Drawer>
  );
}
